import { Grid } from "@mui/material";
import { useState } from "react";
import { ExternalUrls } from "../../../../lib/constants";
import { PricingTiers } from "../../../../lib/pricing-data";
import { formatPrice, roundPrice, calculateSavings } from "../../../../lib/pricing-utils";
import { PricingContent } from "../../../../lib/pricing-content";
import { ProofBarData } from "../../../../lib/social-proof";
import { getFAQs } from "../../../../lib/faqs";
import { useFAQSchema, useProductSchema, ProductOffer } from "../../../../lib/seo";
import { useCurrency } from "../../../../contexts/CurrencyContext";

import { Section } from "../../../ui/Section";
import { SectionHeader } from "../../../ui/SectionHeader";
import { FadeIn } from "../../../ui/FadeIn";
import { PricingCard } from "../../../ui/PricingCard";
import { StepsGrid } from "../../../ui/StepsGrid"; // Was HowItWorksSection
import { PricingTabHeader } from "../PricingTabHeader";
import { PricingAddonStrip } from "../PricingAddonStrip";
import { PlanComparison } from "../PlanComparison";
import { FounderStory } from "../FounderStory";
import { PricingContentLayout } from "../../PricingContentLayout";
import { PricingSocialProofSection } from "../../PricingSocialProofSection";
import { FAQSection } from "../../FAQSection";
import { CallToActionCard } from "../../../ui/CallToActionCard";



export function PricingIndividualsView() {
    const { currency } = useCurrency();
    const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
    const content = PricingContent.individuals;
    const faqs = getFAQs('pricing-client');
    useFAQSchema(faqs);

    const isAnnual = billingPeriod === "annual";

    // Product schema for SEO
    const productOffers: ProductOffer[] = [
        {
            name: 'Neumind Active',
            description: 'Full clinician connection and advanced tools for active rehabilitation.',
            price: isAnnual ? PricingTiers.active.annual[currency] : PricingTiers.active.monthly[currency],
            priceCurrency: currency,
            availability: 'InStock',
            url: '/pricing'
        },
        {
            name: 'Neumind Life',
            description: 'For maintaining habits and managing daily life post-discharge.',
            price: isAnnual ? PricingTiers.life.annual[currency] : PricingTiers.life.monthly[currency],
            priceCurrency: currency,
            availability: 'InStock',
            url: '/pricing'
        }
    ];
    useProductSchema(productOffers);

    return (
        <>
            <Section centered py={6} maxWidth="xl">
                <PricingTabHeader
                    title={content.header.title}
                    description={content.header.description}
                    proofBarItems={ProofBarData.individuals}
                    textMb={2}
                />



                <PricingContentLayout
                    maxWidth={10}
                    billingPeriod={billingPeriod}
                    onBillingPeriodChange={setBillingPeriod}
                >
                    {/* Neumind Active */}
                    <Grid item xs={12} md={5}>
                        <FadeIn delay={100} direction="up" style={{ height: '100%' }}>
                            <PricingCard
                                overhead="Active Rehab"
                                title="Neumind Active"
                                caption={PricingTiers.active.bestFor}
                                highlighted={true}
                                highlightText="Most Popular"
                                themeColor="audience.individual.main"
                                price={
                                    isAnnual
                                        ? formatPrice(roundPrice(PricingTiers.active.annual[currency] / 12), currency)
                                        : formatPrice(roundPrice(PricingTiers.active.monthly[currency]), currency)
                                }
                                priceSuffix="/ month"
                                savings={
                                    isAnnual
                                        ? calculateSavings(PricingTiers.active.monthly[currency] * 12, PricingTiers.active.annual[currency])
                                        : undefined
                                }
                                priceNote={isAnnual
                                    ? `Billed annually as ${formatPrice(PricingTiers.active.annual[currency], currency)}`
                                    : 'Billed monthly, cancel anytime'}
                                description="Full clinician connection and advanced tools for active recovery."
                                features={PricingTiers.active.features}
                                excludedFeatures={PricingTiers.active.excludedFeatures}
                                ctaText="Download App"
                                ctaHref={ExternalUrls.app}
                                ctaSubText="Includes 14-day free trial"
                            />
                        </FadeIn>
                    </Grid>

                    {/* Neumind Life */}
                    <Grid item xs={12} md={5}>
                        <FadeIn delay={200} direction="up" style={{ height: '100%' }}>
                            <PricingCard
                                overhead="Maintenance"
                                title="Neumind Life"
                                caption={PricingTiers.life.bestFor}
                                themeColor="audience.individual.main"
                                price={
                                    isAnnual
                                        ? formatPrice(roundPrice(PricingTiers.life.annual[currency] / 12), currency)
                                        : formatPrice(roundPrice(PricingTiers.life.monthly[currency]), currency)
                                }
                                priceSuffix="/ month"
                                priceNote={isAnnual
                                    ? `Billed annually as ${formatPrice(PricingTiers.life.annual[currency], currency)}`
                                    : `Billed monthly, cancel anytime`
                                }
                                description="For maintaining habits and managing daily life post-discharge."
                                features={PricingTiers.life.features}
                                excludedFeatures={PricingTiers.life.excludedFeatures}
                                ctaText="Download App"
                                ctaHref={ExternalUrls.app}
                                ctaVariant="outlined"
                                ctaSubText="Includes 14-day free trial"
                            />
                        </FadeIn>
                    </Grid>
                </PricingContentLayout>
            </Section>

            {/* Stats Section 1 (Individuals) */}
            <Section variant="dark" py={8}>
                <SectionHeader
                    title="Trusted by families"
                    subtitle="Helping survivors and their families reclaim independence."
                    variant="dark"
                    centered
                    sx={{ mb: 8 }}
                />
                <PricingSocialProofSection
                    userType={['patient', 'carer']}
                    metricIds={['taskAdherence', 'independence']}
                    bgVariant="dark"
                />
            </Section>

            {/* 4. Feature Comparison */}
            <Section variant="muted" py={6}>
                <SectionHeader
                    title="What's the difference?"
                    subtitle="Both plans include full app access. The main difference is professional support."
                    centered
                    sx={{ mb: 4 }}
                />
                <PlanComparison />
            </Section>

            {/* How it works section */}
            <Section variant="default" py={10}>
                <FadeIn direction="up">
                    <SectionHeader
                        title="How It Works"
                        subtitle="Getting started is simple. You can join via an invite or sign up yourself."
                        centered
                    />
                </FadeIn>
                <StepsGrid
                    steps={content.steps}
                />
            </Section>

            {/* 6. Add-Ons */}
            <Section variant="muted" py={8}>
                <FadeIn direction="up">
                    <SectionHeader
                        overline="Optional Add-ons"
                        title="Enhance your experience"
                        subtitle="Optional add-ons to help you get the most out of Neumind."
                        centered
                        titleVariant="h2"
                    />
                </FadeIn>
                <PricingAddonStrip data={content.addOns} />
            </Section>

            {/* 7. Founder Story */}
            <Section variant="default" py={10}>
                <FounderStory />
            </Section>

            {/* Common Questions */}
            <Section variant="muted" py={8}>
                <SectionHeader
                    title="Common Questions"
                    centered
                    sx={{ mb: 8 }}
                />
                <FAQSection faqs={faqs} />
            </Section>

            {/* CTA */}
            <Section py={12}>
                <CallToActionCard
                    location="pricing_individuals_bottom_cta"
                    title="Ready to begin your recovery journey?"
                    description="Join thousands of survivors and families using Neumind to reclaim independence."
                    primaryCTA={{ label: "Start Free Trial", href: ExternalUrls.dashboardSignup, external: true }}
                    secondaryCTA={{ label: "Contact Us", href: "/contact-us" }}
                />
            </Section>
        </>
    );
}
